// Scan resolution against the device's copy of the registry (R3.2, §3.3, R7.7): what a
// code refers to — a location, a logistic unit, a trade item of a known product, or
// nothing the registry knows — and what the container holds (C2: quantity with source).

import { HandlingProfileConfig, identityKey, isBulk, rulesForProduct, uuidv7, type EffectiveRules, type GS1Elements, type Quantity, type ScanInput, SiteConfig } from "@trail/core";
import type { BundleGtin, BundleLocation, BundleProduct, ReferenceBundle } from "../api/client.js";

const BUNDLE_KEY = "trail.referenceBundle";

/** The device's cached bundle may predate settings added since; parsing the site config
 *  through the schema fills every default, so a stale cache never crashes a screen. */
export function loadCachedBundle(): ReferenceBundle | null {
  try {
    const raw = localStorage.getItem(BUNDLE_KEY);
    if (!raw) return null;
    const b = JSON.parse(raw) as ReferenceBundle;
    const parsed = SiteConfig.safeParse(b.siteConfig ?? {});
    return { ...b, siteConfig: parsed.success ? parsed.data : SiteConfig.parse({}) };
  } catch {
    return null;
  }
}

export function cacheBundle(b: ReferenceBundle): void {
  try {
    localStorage.setItem(BUNDLE_KEY, JSON.stringify(b));
  } catch {
    /* too large for this browser's quota: the in-memory copy still works this session */
  }
}

export interface Registry {
  bundle: ReferenceBundle;
  byGtin: Map<string, { product: BundleProduct; info: BundleGtin }>;
  locationByCode: Map<string, BundleLocation>;
  locationByGln: Map<string, BundleLocation>;
  receivingLocations: BundleLocation[];
  /** Identities in stock at bundle time (offline collision check). */
  stockKeys: Set<string>;
  /** The site's default profile (the named one when published, else the shipped lot-tracked profile). */
  siteDefault: { name: string; version: number; config: HandlingProfileConfig };
}

/** The rules for a product as scanned: company default → site default → the company's chosen
 *  dimension's rule for the product's value → the article's own profile, each level able to
 *  lock the ones below (owner 2026-10-30); then the supplier's label overrides. */
export function rulesFor(reg: Registry, product: BundleProduct | null, info: BundleGtin | null): EffectiveRules {
  return rulesForProduct(
    { profiles: reg.bundle.profiles, tenantProfiles: reg.bundle.tenantConfig?.profiles ?? null, siteProfiles: { defaultProfile: reg.bundle.siteConfig?.defaultProfile ?? null, allowBelow: reg.bundle.siteConfig?.profiles?.allowBelow ?? false }, rules: reg.bundle.rules, fallback: reg.siteDefault },
    product,
    info?.captureOverrides ?? null,
  );
}

/** A product handled as a quantity without identity (profile unit = bulk, owner 2026-11-03). */
export function isBulkProduct(reg: Registry, product: BundleProduct | null): boolean {
  return isBulk(rulesFor(reg, product, null));
}

export function buildRegistry(bundle: ReferenceBundle): Registry {
  const byGtin = new Map<string, { product: BundleProduct; info: BundleGtin }>();
  for (const product of bundle.products) for (const info of product.gtins) byGtin.set(info.gtin, { product, info });
  const locationByCode = new Map<string, BundleLocation>();
  const locationByGln = new Map<string, BundleLocation>();
  for (const l of bundle.locations) {
    locationByCode.set(l.code.toUpperCase(), l);
    locationByCode.set(l.path.toUpperCase(), l);
    if (l.glnExtension) locationByGln.set(l.glnExtension, l);
  }
  const named = bundle.profiles?.find((p) => p.name === bundle.siteConfig?.defaultProfile);
  const siteDefault = named ?? { name: bundle.siteConfig?.defaultProfile ?? "Standard", version: 0, config: HandlingProfileConfig.parse({}) };
  return { bundle, byGtin, locationByCode, locationByGln, receivingLocations: bundle.locations.filter((l) => l.roles.includes("receiving")), stockKeys: new Set(bundle.stockKeys ?? []), siteDefault };
}

/** Deviations the resolution itself can see (chapter 4 §4.5); the rest come from the flow. */
export type ScanDeviation = "unknown-product" | "missing-lot" | "missing-best-before" | "quantity-unknown" | "quantity-missing";

/** A scan as the host hands it to the interpreter, with what the registry knew about it. */
export interface ResolvedScan {
  input: ScanInput;
  raw: string;
  symbologyId: string | null;
  assembled: boolean;
  location: BundleLocation | null;
  product: BundleProduct | null;
  gtinInfo: BundleGtin | null;
  quantity: Quantity | null;
  deviations: ScanDeviation[];
  /** True for an SSCC this device had issued for a pallet it is building. */
  issuedHere: boolean;
  /** How the code reached the pipeline (R7.6 capture source). */
  source?: "wedge" | "camera" | "manual" | undefined;
  /** The operator's comment when the facts were typed instead of scanned (owner 2026-11-01). */
  note?: string | undefined;
  /** The rules that applied (null for locations and pallets). */
  rules?: EffectiveRules | undefined;
  /** A bulk product (no identity, owner 2026-11-03): the scan names the product, the operator types the quantity. */
  bulk?: boolean | undefined;
  /** Values the operator entered at the pallet (mockup follow-ups A and B). */
  entered?: EnteredData | undefined;
}

export interface EnteredData {
  serial?: string;
  bestBefore?: string;
  bestBeforeSource?: string;
  /** The quantity the operator accepted when the barcode had none, and the standard it was compared with. */
  quantity?: string;
  quantityStandard?: string | null;
}

/** The same scan re-keyed with a serial that tells it apart from the container in stock. */
export function withSerial(scan: ResolvedScan, serial: string): ResolvedScan {
  const e = scan.input.parsed;
  const r = scan.input.resolved;
  if (r.as !== "container" || !e.gtin) return scan;
  const parsed: GS1Elements = { ...e, ai: { ...e.ai, "21": serial }, serial };
  const identityKey = e.lot ? `gtin+lot+serial:01=${e.gtin};10=${e.lot};21=${serial}` : `gtin+serial:01=${e.gtin};21=${serial}`;
  return { ...scan, input: { ...scan.input, parsed, resolved: { ...r, identityKey } }, entered: { ...scan.entered, serial } };
}

/** The same scan with a best-before the operator read off the goods, and where from. */
export function withBestBefore(scan: ResolvedScan, iso: string, source: string): ResolvedScan {
  const e = scan.input.parsed;
  const parsed: GS1Elements = { ...e, ai: { ...e.ai, "15": iso.replace(/-/g, "").slice(2) }, bestBefore: iso };
  return { ...scan, input: { ...scan.input, parsed }, deviations: scan.deviations.filter((d) => d !== "missing-best-before"), entered: { ...scan.entered, bestBefore: iso, bestBeforeSource: source } };
}

/** The same scan with a quantity the operator accepted (the standard, confirmed) or typed; the base unit of the product. */
export function withQuantity(scan: ResolvedScan, value: string, standard: string | null): ResolvedScan {
  const uom = scan.product?.baseUom ?? scan.quantity?.uom ?? "EA";
  if (scan.bulk) return { ...scan, quantity: { value: decimal(Number(value)), uom, source: "entered" }, entered: { ...scan.entered, quantity: value, quantityStandard: null } };
  const source: Quantity["source"] = standard !== null && Number(value) === Number(standard) ? "standard" : "entered";
  return { ...scan, quantity: { value: decimal(Number(value)), uom, source }, deviations: scan.deviations.filter((d) => d !== "quantity-missing" && d !== "quantity-unknown"), entered: { ...scan.entered, quantity: value, quantityStandard: standard } };
}

/** Codes an operator may scan to say "done" where no location is at hand. */
const DONE_CODES = new Set(["DONE", "KLAR", "END", "SLUT"]);

/** Minimal element set for a code that carries no GS1 data (a plain Code 128 location label). */
export function plainElements(text: string, symbologyId: string | null): GS1Elements {
  return { ai: {}, symbologyId, digitalLink: false, gtin: null, containedGtin: null, sscc: null, lot: null, serial: null, productionDate: null, packagingDate: null, bestBefore: null, expiry: null, count: null, netWeightKg: null, lengthM: null, hri: [text] };
}

/** A location by its scanned code or path. A keyboard-wedge scanner set to a US layout on a
 *  phone whose hardware-keyboard layout is Swedish delivers "-" as "+" (owner 2026-10-29:
 *  "HA+1+1+2" for HA-1-1-2), so a miss is retried with that swap. The fix proper is the
 *  scanner's keyboard-language setting; this keeps the code usable meanwhile. */
export function findLocation(reg: Registry, text: string): BundleLocation | null {
  const code = text.trim().toUpperCase();
  return reg.locationByCode.get(code) ?? (code.includes("+") ? reg.locationByCode.get(code.replace(/\+/g, "-")) ?? null : null);
}

export function resolvePlain(reg: Registry, text: string, symbologyId: string | null, raw: string): ResolvedScan {
  const code = text.trim().toUpperCase();
  const location = findLocation(reg, text);
  const parsed = plainElements(text, symbologyId);
  const base = { raw, symbologyId, assembled: false, location, product: null, gtinInfo: null, quantity: null, deviations: [] as ScanDeviation[], issuedHere: false };
  if (location) return { ...base, input: { kind: "scan", parsed, resolved: { as: "location", locationId: location.id, roles: location.roles } } };
  if (DONE_CODES.has(code)) return { ...base, input: { kind: "scan", parsed, resolved: { as: "done-code" } } };
  return { ...base, input: { kind: "scan", parsed, resolved: { as: "unknown" } } };
}

/** What to do with a trade-item code before resolving it: hold for the label's other code,
 *  take it as it is, or refuse — from the product's rules (capture.lot, partialCodes). */
export function partialPolicy(reg: Registry, e: GS1Elements): { complete: boolean; action: "accept" | "hold" | "reject" } {
  if (!e.gtin) return { complete: true, action: "accept" };
  const hit = reg.byGtin.get(e.gtin) ?? null;
  const rules = rulesFor(reg, hit?.product ?? null, hit?.info ?? null);
  const complete = identityKey(rules.identification.scheme, e) !== null;
  if (complete || rules.capture.lot === "forbidden" || rules.capture.lot === "optional") return { complete: true, action: "accept" };
  const policy = rules.capture.partialCodes;
  return { complete: false, action: policy === "accept-alone" ? "accept" : policy === "reject" ? "reject" : "hold" };
}

export function resolveGs1(reg: Registry, e: GS1Elements, raw: string, opts: { assembled: boolean; issuedSsccs: ReadonlySet<string> }): ResolvedScan {
  const base = { raw, symbologyId: e.symbologyId, assembled: opts.assembled, location: null, product: null, gtinInfo: null, quantity: null, deviations: [] as ScanDeviation[], issuedHere: false };
  const ext = e.ai["254"];
  if (ext && reg.locationByGln.has(ext)) {
    const location = reg.locationByGln.get(ext)!;
    return { ...base, location, input: { kind: "scan", parsed: e, resolved: { as: "location", locationId: location.id, roles: location.roles } } };
  }
  if (e.sscc) {
    return {
      ...base,
      issuedHere: opts.issuedSsccs.has(e.sscc),
      quantity: { value: "1", uom: "EA", source: "scanned" },
      input: { kind: "scan", parsed: e, resolved: { as: "container", containerId: uuidv7(), containerKind: "logistic-unit", identityKey: `sscc:00=${e.sscc}` } },
    };
  }
  if (e.gtin) {
    const hit = reg.byGtin.get(e.gtin) ?? null;
    const rules = rulesFor(reg, hit?.product ?? null, hit?.info ?? null);
    const deviations: ScanDeviation[] = [];
    if (!hit) deviations.push("unknown-product");
    // A bulk product (owner 2026-11-03): the barcode names the product, nothing more. No identity, no
    // lot, no deviation for the missing ones; the quantity is typed by the operator, never proposed.
    if (hit && isBulk(rules)) {
      return {
        ...base,
        product: hit.product,
        gtinInfo: hit.info,
        quantity: null,
        deviations,
        rules,
        bulk: true,
        input: { kind: "scan", parsed: e, resolved: { as: "container", containerId: uuidv7(), containerKind: "loose", identityKey: null } },
      };
    }
    const key = identityKey(rules.identification.scheme, e);
    if (key === null && rules.capture.lot === "required") deviations.push("missing-lot");
    if (rules.capture.bestBefore === "required" && !e.bestBefore && !e.expiry) deviations.push("missing-best-before");
    const quantity = quantityOf(e, hit?.info ?? null, hit?.product ?? null);
    // No quantity in the barcode (owner 2026-11-03): the carton never counts until the operator has seen
    // and accepted a quantity — the register's standard for a batch-handled article, typed otherwise.
    if (rules.capture.netContent !== "forbidden" && quantity.source === "inferred" && quantity.value === "1" && !hit?.info.containsQty) deviations.push(hit ? "quantity-missing" : "quantity-unknown");
    return {
      ...base,
      product: hit?.product ?? null,
      gtinInfo: hit?.info ?? null,
      quantity,
      deviations,
      rules,
      input: { kind: "scan", parsed: e, resolved: { as: "container", containerId: uuidv7(), containerKind: "trade-item", identityKey: key } },
    };
  }
  return { ...base, input: { kind: "scan", parsed: e, resolved: { as: "unknown" } } };
}

/** The container's content as the label states it (C2: the carrier's quantity is the
 *  truth at receiving), read through the supplier product's mapping when there is one. */
export function quantityOf(e: GS1Elements, info: BundleGtin | null, product: BundleProduct | null): Quantity {
  const qf = info?.quantityFrom;
  if (qf) {
    const v = valueForAi(e, qf.ai);
    if (v !== null) return { value: decimal(v), uom: qf.uom, source: "scanned" };
  }
  if (e.lengthM !== null) return { value: decimal(e.lengthM), uom: "MTR", source: "scanned" };
  if (e.netWeightKg !== null) return { value: decimal(e.netWeightKg), uom: "KGM", source: "scanned" };
  if (e.count !== null) return { value: decimal(e.count), uom: "EA", source: "scanned" };
  if (info?.containsQty && product) return { value: info.containsQty, uom: product.baseUom, source: "inferred" };
  return { value: "1", uom: "EA", source: "inferred" }; // one container; content unknown
}

function valueForAi(e: GS1Elements, ai: string): number | null {
  if (ai.startsWith("311")) return e.lengthM;
  if (ai.startsWith("310")) return e.netWeightKg;
  if (ai === "30" || ai === "37") return e.count;
  const raw = e.ai[ai];
  if (raw === undefined) return null;
  // Other measure AIs (3nnX): the last digit of the AI is the number of decimals.
  if (/^3\d{3}$/.test(ai) && /^\d+$/.test(raw)) return Number(raw) / 10 ** Number(ai[3]);
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

function decimal(n: number): string {
  // Fixed notation without exponent; up to 6 decimals, trailing zeros trimmed.
  const s = n.toFixed(6).replace(/\.?0+$/, "");
  return s === "" || s === "-" ? "0" : s;
}

/** §4.5 identity collision: is this identity already in stock? Online, the ledger answers
 *  (with a short timeout so a scan burst never waits long); offline, the bundle's snapshot.
 *  Returns where the existing container stands, or null when there is no collision. */
export async function checkCollision(reg: Registry, identityKey: string, lookup: (key: string, timeoutMs: number) => Promise<{ location_id: string | null; received_at?: string | null }[]>): Promise<{ collision: true; locationCode: string | null; receivedAt: string | null } | null> {
  if (navigator.onLine) {
    try {
      const rows = await lookup(identityKey, 1500);
      if (rows.length === 0) return null;
      const loc = rows[0]?.location_id ? reg.bundle.locations.find((l) => l.id === rows[0]!.location_id) : undefined;
      return { collision: true, locationCode: loc?.code ?? null, receivedAt: rows[0]?.received_at ?? null };
    } catch {
      /* fall through to the snapshot */
    }
  }
  return reg.stockKeys.has(identityKey) ? { collision: true, locationCode: null, receivedAt: null } : null;
}

/** The storage class a location has: its own, else the nearest ancestor's (owner 2026-10-31: temperature). */
/** What a location takes (owner 2026-11-02: racking has beams, cartons must sit on pallets): the
 *  location's own `holds`, else the nearest parent's, else any. */
export type Holds = "pallets" | "loose" | "any";
export function holdsOf(reg: Registry, locationId: string): Holds {
  const byPath = new Map(reg.bundle.locations.map((l) => [l.path, l]));
  let l = reg.bundle.locations.find((x) => x.id === locationId);
  while (l) {
    if (l.holds === "pallets" || l.holds === "loose" || l.holds === "any") return l.holds;
    const pp = l.path.includes("/") ? l.path.slice(0, l.path.lastIndexOf("/")) : null;
    l = pp ? byPath.get(pp) : undefined;
  }
  return "any";
}

/** Pallets a location takes (0 = no limit): its own value, else the nearest parent's, else no limit. */
export function palletCapacityOf(reg: Registry, locationId: string): number {
  const byPath = new Map(reg.bundle.locations.map((l) => [l.path, l]));
  let l = reg.bundle.locations.find((x) => x.id === locationId);
  while (l) {
    if (typeof l.palletCapacity === "number") return l.palletCapacity;
    const pp = l.path.includes("/") ? l.path.slice(0, l.path.lastIndexOf("/")) : null;
    l = pp ? byPath.get(pp) : undefined;
  }
  return 0;
}

export function storageClassOf(reg: Registry, locationId: string): string | null {
  const byPath = new Map(reg.bundle.locations.map((l) => [l.path, l]));
  let l = reg.bundle.locations.find((x) => x.id === locationId);
  while (l) {
    if (l.storageClass) return l.storageClass;
    const pp = l.path.includes("/") ? l.path.slice(0, l.path.lastIndexOf("/")) : null;
    l = pp ? byPath.get(pp) : undefined;
  }
  return null;
}
/** The first mismatch among several products — a pallet is checked by its contents (owner 2026-10-31). */
export function storageMismatchAny(reg: Registry, productIds: (string | null | undefined)[], locationId: string): { need: string; has: string; action: "warn" | "refuse" } | null {
  for (const pid of productIds) { const m = storageMismatch(reg, pid ?? null, locationId); if (m) return m; }
  return null;
}
/** A product going onto a location of another class than it needs: the names, and what the site does about it. */
export function storageMismatch(reg: Registry, productId: string | null, locationId: string): { need: string; has: string; action: "warn" | "refuse" } | null {
  const cfg = (reg.bundle.siteConfig as unknown as { locations?: { storageCheck?: "off" | "warn" | "refuse"; storageClasses?: { key: string; name: string }[] } } | null)?.locations;
  const action = cfg?.storageCheck ?? "warn";
  if (action === "off" || !productId) return null;
  const need = reg.bundle.products.find((p) => p.id === productId)?.storageRequirement ?? null;
  if (!need) return null;
  const has = storageClassOf(reg, locationId);
  if (has === need) return null;
  const name = (k: string | null) => (k ? cfg?.storageClasses?.find((c) => c.key === k)?.name ?? k : "—");
  return { need: name(need), has: name(has), action };
}

